/**
 * What both shells render around their routes (see `App.tsx`): the outage
 * bar, the stale-client updater, and the Sentry-instrumented `<Routes>`.
 */
import * as Sentry from '@sentry/react';
import { usePostHog } from 'posthog-js/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes } from 'react-router';

import { useAutoUpdate } from './lib/auto-update/use-auto-update';

export const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

export const OutageBar = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();

  if (!posthog.getFeatureFlag('down-banner')) {
    return <>{children}</>;
  }

  return (
    <>
      <div
        className="fixed left-0 right-0 top-0 z-[999999] flex w-full flex-col items-center justify-center gap-2 bg-alert-500 px-4 py-2 text-center font-semibold text-white shadow-lg md:flex-row"
        style={{ letterSpacing: '0.01em' }}>
        <span>
          {t('outageBar.message')}
          <a
            href="https://www.cloudflarestatus.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="ml-1 font-bold underline">
            {t('outageBar.checkStatus')}
          </a>
          .
        </span>
        <span className="ml-2">
          <a
            href="https://discord.gg/eafSXxrB3F"
            target="_blank"
            rel="noopener noreferrer"
            className="font-bold underline">
            {t('outageBar.joinDiscord')}
          </a>
        </span>
      </div>
      <div className="pt-12">{children}</div>
    </>
  );
};

/**
 * Drives the stale-client auto-updater. Renders nothing; must live inside the
 * Router + PostHog providers (it reads location + refreshes feature flags).
 */
export const AutoUpdater = () => {
  useAutoUpdate();
  return null;
};

/** The toaster both shells mount, styled the same in each. */
export const TOAST_OPTIONS = {
  classNames: {
    error: 'bg-alert-50 border-alert-300 text-alert-950',
    success: 'bg-valid-200 text-valid-950 border-valid-300 ',
  },
};
